import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/atoms/Card';
import InfoTip from '@/components/atoms/InfoTip';
import { Switch } from '@/components/atoms/Switch';
import ContentBlock from '@/components/molecules/ContentBlock';

import { PositionValue } from '../PositionValue/PositionValue';

import { MeasurementActions } from './MeasurementActions';
import {
    type MeasurementLimits,
    type SavedMeasurement,
    useMeasurementDraft,
} from './useMeasurementDraft';

import styles from './MeasurementCard.module.scss';

interface MeasurementCardProps {
    distance: number;
    depth: number;
    minDepth: number;
    maxDepth: number;
    scanEnabled: boolean;
    scanRunning?: boolean;
    canMove: boolean;
    moveDisabledReason: string;
    distanceLimits: MeasurementLimits;
    depthLimits: MeasurementLimits;
    onSave: (values: SavedMeasurement) => void;
    onToggleScan: (enabled: boolean) => void;
    onMove: () => void;
    onStop: () => void;
}

export function MeasurementCard(props: MeasurementCardProps) {
    const {
        distance,
        depth,
        minDepth,
        maxDepth,
        scanEnabled,
        scanRunning = false,
        canMove,
        moveDisabledReason,
        distanceLimits,
        depthLimits,
        onSave,
        onToggleScan,
        onMove,
        onStop,
    } = props;
    const { t } = useTranslation();
    const distanceId = useId();
    const depthId = useId();
    const minId = useId();
    const maxId = useId();
    const { isEditing, shown, errors, draftInRange, setField, startEdit, cancelEdit, saveEdit } =
        useMeasurementDraft({
            saved: { distance, depth, minDepth, maxDepth },
            scanEnabled,
            distanceLimits,
            depthLimits,
            onSave,
        });
    const { distanceError, depthError, minError, maxError } = errors;
    const unit = t('winchControl.position.meters');

    return (
        <ContentBlock
            shrink
            title={t('winchControl.card.measurement')}
            headerActions={
                <InfoTip
                    label={t('winchControl.card.measurementHelp')}
                    content={t('winchControl.card.measurementTooltip')}
                />
            }>
            <div className={styles.body}>
                <div className={styles.values}>
                    <PositionValue
                        label={t('winchControl.card.distance')}
                        value={shown.distance}
                        unit={unit}
                        muted={scanEnabled}
                        editing={isEditing && !scanEnabled}
                        inputId={distanceId}
                        caption={distanceError}
                        invalid={distanceError != null}
                        onChange={(value) => setField('distance', value)}
                    />
                    <PositionValue
                        label={t('winchControl.card.depth')}
                        value={shown.depth}
                        unit={unit}
                        muted={scanEnabled}
                        editing={isEditing && !scanEnabled}
                        inputId={depthId}
                        caption={depthError}
                        invalid={depthError != null}
                        onChange={(value) => setField('depth', value)}
                    />
                </div>
                <Card variant="subtle" radius="sm" padding="none" className={styles.scanBox}>
                    <div className={styles.scanToggle}>
                        <Switch
                            label={t('winchControl.card.verticalScan')}
                            checked={scanEnabled}
                            onCheckedChange={onToggleScan}
                        />
                        <InfoTip
                            label={t('winchControl.card.verticalScanHelp')}
                            content={t('winchControl.card.verticalScanTooltip')}
                        />
                    </div>
                    {scanEnabled && (
                        <div className={styles.values}>
                            <PositionValue
                                label={t('winchControl.card.minDepth')}
                                value={shown.minDepth}
                                unit={unit}
                                nested
                                editing={isEditing}
                                inputId={minId}
                                caption={minError}
                                invalid={minError != null}
                                onChange={(value) => setField('minDepth', value)}
                            />
                            <PositionValue
                                label={t('winchControl.card.maxDepth')}
                                value={shown.maxDepth}
                                unit={unit}
                                nested
                                editing={isEditing}
                                inputId={maxId}
                                caption={maxError}
                                invalid={maxError != null}
                                onChange={(value) => setField('maxDepth', value)}
                            />
                        </div>
                    )}
                </Card>
                <div className={styles.actions}>
                    <MeasurementActions
                        isEditing={isEditing}
                        canSave={draftInRange}
                        scanEnabled={scanEnabled}
                        scanRunning={scanRunning}
                        canMove={canMove}
                        moveDisabledReason={moveDisabledReason}
                        onEdit={startEdit}
                        onCancel={cancelEdit}
                        onSave={saveEdit}
                        onMove={onMove}
                        onStop={onStop}
                    />
                </div>
            </div>
        </ContentBlock>
    );
}
